import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { TableDataVersion } from '../../../../common/table/TableTypes';
import type { MNTableContent, TableContentData } from '../../../../common/table/TableTypes';
import { asObject, ImMap, prop } from '../../../../common/utils/immutableHelper';
import { getIsFeatureEnabledForCurrentUser } from '../../feature/elementFeatureSelector';
import { ExperimentId } from '../../../../common/experiments/experimentsConstants';
import { getStoredHotTableDataVersion, isHotTableDataV2 } from './hotTableDataVersionUtils';
import {
    convertTableContentDataToVersion2,
    normalizeTableContentDataDraftContent,
} from './hotTableDataConversionUtils';
import logger from '../../../logger';
import { convertReduxValueToHotValue } from '../utils/dataUtils/persistence/tablePersistenceValueUtils';
import { userLanguagePreferenceSelector } from '../../../user/currentUserSelector';

/**
 * Gets the table content data in Version 2 format, if the feature is enabled or
 * the data is already in V2 format.
 */
export const useHotTableDataV2 = (
    tableContent: MNTableContent | ImMap<MNTableContent> | null | undefined,
    // Used entirely for logging purposes
    elementId?: string,
): TableContentData | undefined => {
    const locale = useSelector(userLanguagePreferenceSelector);

    const imTableContentData = prop('data', tableContent);
    const storedVersion = getStoredHotTableDataVersion(tableContent);
    const isTableDataV2Enabled = useSelector(getIsFeatureEnabledForCurrentUser(ExperimentId.tableArchV2));

    const tableContentDataV2 = useMemo(() => {
        // If the table is explicitly V1 we want to use the legacy table, rather than the new V2 table, even if the
        //  user has V2 architecture enabled. This is for debugging purposes
        if (storedVersion === TableDataVersion.V1) return undefined;

        const tableContentData = asObject<TableContentData>(imTableContentData);

        if (isHotTableDataV2(tableContent)) return normalizeTableContentDataDraftContent(tableContentData);

        if (!isTableDataV2Enabled) return undefined;

        try {
            // Now we need to convert from V1 to V2
            return convertTableContentDataToVersion2(tableContentData);
        } catch (e) {
            logger.error('Failed to convert table content data to Version 2', e, { elementId });
            return undefined;
        }
    }, [isTableDataV2Enabled, storedVersion, imTableContentData]);

    // Convert the table content data from the redux format to the HoT Table format
    // TODO - WEB-14572 - This might be unnecessary if we store both formats or render to the locale at the cell level
    return useMemo(() => {
        if (!tableContentDataV2) return undefined;

        return tableContentDataV2.map((rows) => rows.map((cell) => convertReduxValueToHotValue(cell, locale)));
    }, [tableContentDataV2, locale]);
};
